import React, { useEffect, useState } from "react";
import { SwissDateField } from "@/components/SwissDateInput";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Wallet } from "lucide-react";
import { EmptyState } from "@/components/EmptyState";
import { formatDate } from "@/lib/formatDate";

export function Cashbook() {
  const [transactions, setTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({
    date: "",
    description: "",
    amount: "",
    currency: "CHF",
    direction: "in",
    category_account_id: "",
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [postingId, setPostingId] = useState(null);
  const [error, setError] = useState("");

  const companyId = localStorage.getItem("demoCompanyId");

  const loadData = async () => {
    if (!companyId) return;
    setLoading(true);
    setError("");
    try {
      const [txRes, accRes] = await Promise.all([
        apiClient.get(`/companies/${companyId}/cash-transactions`),
        apiClient.get(`/companies/${companyId}/accounts`),
      ]);
      setTransactions(txRes.data || []);
      setAccounts(accRes.data || []);
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || "Fehler beim Laden des Kassenbuchs.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!companyId) return;
    if (!form.date || !form.description || !form.amount || !form.category_account_id) {
      setError("Bitte Datum, Beschreibung, Betrag und Konto angeben.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = {
        date: form.date,
        description: form.description,
        amount: parseFloat(form.amount) || 0,
        currency: form.currency,
        direction: form.direction,
        category_account_id: form.category_account_id,
      };
      await apiClient.post(`/companies/${companyId}/cash-transactions`, payload);
      setForm({ date: "", description: "", amount: "", currency: form.currency, direction: "in", category_account_id: "" });
      await loadData();
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || "Fehler beim Erfassen der Kassenbewegung.");
    } finally {
      setSaving(false);
    }
  };

  const handlePost = async (txId) => {
    setPostingId(txId);
    setError("");
    try {
      await apiClient.post(`/cash-transactions/${txId}/post`);
      await loadData();
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || "Fehler beim Buchen der Kassenbewegung.");
    } finally {
      setPostingId(null);
    }
  };

  const categoryOptions = accounts.filter((a) => ["income", "expense"].includes(a.type));

  return (
    <div className="space-y-6" data-testid="cashbook-page">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Kasse</h1>
        <p className="mt-1 text-sm text-slate-500">
          Erfassen Sie Bareinnahmen und Barausgaben. Buchungen werden erst mit Klick auf "Buchen" ins Journal übernommen.
        </p>
      </div>

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
          {error}
        </div>
      )}

      <Card className="p-4 max-w-2xl border border-slate-200 bg-white shadow-sm" data-testid="cashbook-form-card">
        <form className="space-y-3" onSubmit={handleCreate} data-testid="cashbook-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label htmlFor="cash-date">Datum</Label>
              <SwissDateField
                id="cash-date"
                
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="cashbook-date-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="cash-amount">Betrag</Label>
              <Input
                id="cash-amount"
                type="number"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                data-testid="cashbook-amount-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="cash-direction">Typ</Label>
              <Select
                value={form.direction}
                onValueChange={(value) => setForm({ ...form, direction: value })}
              >
                <SelectTrigger id="cash-direction" data-testid="cashbook-direction-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="in">Einnahme</SelectItem>
                  <SelectItem value="out">Ausgabe</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="cash-description">Beschreibung</Label>
            <Input
              id="cash-description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="cashbook-description-input"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="cash-category">Aufwands-/Erlöskonto</Label>
            <Select
              value={form.category_account_id}
              onValueChange={(value) => setForm({ ...form, category_account_id: value })}
            >
              <SelectTrigger id="cash-category" data-testid="cashbook-category-select">
                <SelectValue placeholder="Konto wählen" />
              </SelectTrigger>
              <SelectContent>
                {categoryOptions.map((acc) => (
                  <SelectItem key={acc._id} value={acc.code}>
                    {acc.code} · {acc.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end">
            <Button
              type="submit"
              size="sm"
              className="rounded-full bg-slate-900 text-slate-50 px-4"
              disabled={saving}
              data-testid="cashbook-save-button"
            >
              {saving ? "Speichern ..." : "Kassenbewegung erfassen"}
            </Button>
          </div>
        </form>
      </Card>

      <Card className="p-4 border border-slate-200 bg-white shadow-sm" data-testid="cashbook-list-card">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-medium text-slate-700">Kassenbewegungen</h2>
          {loading && (
            <span className="text-xs text-slate-400" data-testid="cashbook-loading">
              Lade ...
            </span>
          )}
        </div>
        <Table data-testid="cashbook-table">
          <TableHeader>
            <TableRow>
              <TableHead>Datum</TableHead>
              <TableHead>Beschreibung</TableHead>
              <TableHead>Typ</TableHead>
              <TableHead>Betrag</TableHead>
              <TableHead>Konto</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Aktion</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {transactions.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="p-0">
                  <EmptyState
                    testid="cashbook-empty-state"
                    icon={<Wallet className="h-5 w-5" />}
                    title="Noch keine Kassenbewegungen"
                    description="Erfassen Sie Bareinnahmen und -ausgaben — die Gegenbuchung aufs Kassenkonto erfolgt automatisch."
                  />
                </TableCell>
              </TableRow>
            )}
            {transactions.map((tx) => (
              <TableRow key={tx._id} data-testid={`cashbook-row-${tx._id}`}>
                <TableCell>{formatDate(tx.date)}</TableCell>
                <TableCell>{tx.description}</TableCell>
                <TableCell>{tx.direction === "in" ? "Einnahme" : "Ausgabe"}</TableCell>
                <TableCell>
                  {tx.amount.toFixed(2)} {tx.currency}
                </TableCell>
                <TableCell>{tx.category_account_id}</TableCell>
                <TableCell>{tx.posted ? "Gebucht" : "Offen"}</TableCell>
                <TableCell className="text-right">
                  {!tx.posted && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handlePost(tx._id)}
                      disabled={postingId === tx._id}
                      data-testid={`cashbook-post-button-${tx._id}`}
                    >
                      {postingId === tx._id ? "Bucht ..." : "Buchen"}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
